import { useId } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useUser } from '@descope/react-sdk'
import { ChevronRightIcon } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@nxt-shared/ui/components/Alert'
import { Button } from '@nxt-shared/ui/components/Button'
import { Card } from '@nxt-shared/ui/components/Card'
import { ListItem, ListPanel, ListPanelGrid } from '@nxt-shared/ui/components/ListItem'
import { PageContent } from '@nxt-shared/ui/components/PageContent'
import { PageHeader } from '@nxt-shared/ui/components/PageHeader'
import { PageWrapper } from '@nxt-shared/ui/components/PageWrapper'
import { Skeleton } from '@nxt-shared/ui/components/Skeleton'
import {
  useMemberOrganizationGroups,
  liveblogOrganizationUrl,
  nxtCmsOrganizationUrl,
  OrgMark,
  ProductLogo,
  type SwitcherProduct,
  type TenantGroup,
} from '@nxt-platform/global-components'
import { PAGE_BACKDROP } from '../../components/layout/pageBackdrop'
import { usePageTitle } from '../../features/page-title/pageTitle'

export const Route = createFileRoute('/me/')({
  component: TenantListRoute,
})

const ORGANIZATION_COLUMNS = 'grid-cols-[auto_minmax(0,1fr)_auto]'

/** Descope's given name, else the first word of the full name: the profile form stores only the full name. */
function useFirstName(): string | undefined {
  const { user } = useUser()
  return user?.givenName?.trim() || user?.name?.trim().split(/\s+/)[0] || undefined
}

function TenantListRoute() {
  usePageTitle('Organizations')
  const firstName = useFirstName()
  // Liveblog (gatekeeper) and nxt-cms (management) load independently through the shared hook —
  // neither blocks the other, and each keeps its own loading/error/retry state below.
  const { groups, liveblog, nxt } = useMemberOrganizationGroups()

  const liveblogLoading = liveblog.status === 'loading'
  const nxtLoading = nxt.status === 'loading'

  const bothLoading = liveblogLoading && nxtLoading
  const settled = !liveblogLoading && !nxtLoading
  const isEmpty = settled && groups.length === 0 && liveblog.status !== 'error' && nxt.status !== 'error'

  return (
    // `my-auto` centres the column vertically while it fits and lets a longer list start at the top and scroll;
    // `shrink-0` keeps the column from shrinking into a scroll area of its own.
    <div className={`flex flex-1 min-h-0 flex-col overflow-y-auto [scrollbar-gutter:stable] bg-gray-50 px-6 py-8 lg:px-8 xl:py-12 ${PAGE_BACKDROP}`}>
      <PageWrapper className="mx-auto my-auto h-auto w-full max-w-3xl shrink-0">
        <PageHeader
          title={firstName ? `Welcome, ${firstName}` : 'Welcome'}
          description="Where would you like to work today? Pick one of your Liveblog or NXT organizations."
          className="px-0 pt-0 lg:px-0 xl:px-0 xl:pt-0"
          transparent
        />
        <PageContent className="flex-none px-0 pt-0 lg:px-0 xl:px-0 xl:pt-0">
          {bothLoading && <OrganizationsSkeleton />}

          {/* Independent error affordances: one product failing never hides the other. */}
          {liveblog.status === 'error' && (
            <Alert variant="danger" className="mb-6">
              <AlertTitle>Couldn’t load your Liveblog organizations</AlertTitle>
              <AlertDescription>
                <span>{liveblog.error?.message ?? 'Something went wrong.'}</span>
                <Button variant="outline-muted" size="sm" icon={false} className="mt-2" onClick={liveblog.refetch}>
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {nxt.status === 'error' && (
            <Alert variant="danger" className="mb-6">
              <AlertTitle>Couldn’t load your NXT organizations</AlertTitle>
              <AlertDescription>
                <span>{nxt.error?.message ?? 'Something went wrong.'}</span>
                <Button variant="outline-muted" size="sm" icon={false} className="mt-2" onClick={nxt.refetch}>
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {!bothLoading && groups.length > 0 && (
            <div className="flex flex-col gap-6">
              {groups.map((group) => (
                <TenantSection key={group.id} group={group} />
              ))}
            </div>
          )}

          {isEmpty && <EmptyState />}
        </PageContent>
      </PageWrapper>
    </div>
  )
}

function TenantSection({ group }: { group: TenantGroup }) {
  const headingId = useId()
  return (
    <section aria-labelledby={headingId}>
      <ListPanel toolbar={<h3 id={headingId} className="min-w-0 font-bold break-words">{group.name}</h3>}>
        {/* NXT before Liveblog, as in the topbar Organization switcher. */}
        <ListPanelGrid columns={ORGANIZATION_COLUMNS} minWidthClassName="min-w-0">
          {group.nxtOrgs.map((org) => (
            <OrganizationRow
              key={`nxt-${org.id}`}
              product="nxt"
              name={org.name}
              imageUrl={org.imageUrl}
              href={nxtCmsOrganizationUrl(org)}
            />
          ))}
          {group.liveblogOrgs.map((org) => (
            <OrganizationRow
              key={`liveblog-${org.id}`}
              product="liveblog"
              name={org.name}
              imageUrl={org.imageUrl}
              href={liveblogOrganizationUrl(org)}
              shortName={org.shortName}
            />
          ))}
        </ListPanelGrid>
      </ListPanel>
    </section>
  )
}

function OrganizationRow({ product, name, imageUrl, href, shortName }: {
  product: SwitcherProduct
  name: string
  imageUrl?: string
  href: string
  shortName?: string | null
}) {
  return (
    <ListItem
      appearance="row"
      className="group"
      // As wide as "LIVE BLOG" at 20px, so the names line up across tenants whatever logo a panel's widest row has.
      media={<span className="flex w-26"><ProductLogo product={product} /></span>}
      title={
        // The mark stays outside the link, so the link's hover underline doesn't run under the letter tile.
        <span className="flex items-center gap-2">
          <OrgMark name={name} imageUrl={imageUrl} size="sm" />
          <a href={href} className="min-w-0">{name}</a>
        </span>
      }
      meta={shortName ? <span>{shortName}</span> : undefined}
      trailing={<ChevronRightIcon aria-hidden className="size-5 text-muted-foreground transition-colors group-hover:text-foreground" />}
    />
  )
}

function EmptyState() {
  return (
    <Card size="sm" className="items-center gap-1.5 px-6 text-center">
      <p className="font-bold">No organizations yet</p>
      <p className="text-sm text-muted-foreground">
        You don’t have access to any Liveblog or NXT organizations.
      </p>
    </Card>
  )
}

function OrganizationsSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true" aria-label="Loading your organizations">
      {[0, 1].map((section) => (
        <ListPanel key={section} toolbar={<Skeleton className="h-5 w-40" />}>
          <div className="divide-y">
            {[0, 1].map((row) => (
              <div key={row} className="flex items-center gap-5 px-5 py-5">
                <Skeleton className="h-5 w-26 rounded-sm" />
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-28" />
                </div>
              </div>
            ))}
          </div>
        </ListPanel>
      ))}
    </div>
  )
}
